import React, { useState, useEffect } from 'react';
import styles from './NSPDUpdateModal.module.css';
import { RotateIcon, AlertTriangleIcon, CheckCircleIcon, XCircleIcon } from '../../../components/Icon';
import { toast } from 'react-toastify';

const NSPDUpdateModal = ({ isVisible, onClose, feature, onUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [nspdData, setNspdData] = useState(null);
    const [diffs, setDiffs] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isVisible && feature?.properties?.cadastral_number) {
            fetchNSPDData();
        }
    }, [isVisible, feature]);

    const fetchNSPDData = async () => {
        setLoading(true);
        setError(null);
        setNspdData(null);
        setDiffs([]);

        const cadNum = feature.properties.cadastral_number;

        try {
            const response = await fetch(`/api/m/nspd-integration/search?q=${encodeURIComponent(cadNum)}`);
            const data = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                const nspdObj = data.data?.features?.[0];
                if (nspdObj) {
                    setNspdData(nspdObj);
                    compareData(nspdObj);
                } else {
                    setError('Объект не найден в НСПД');
                }
            }
        } catch (err) {
            setError('Ошибка при запросе к НСПД');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const compareData = (nspdObj) => {
        const nspdProps = nspdObj.properties?.options || nspdObj.properties || {};
        const localProps = feature.properties;
        const newDiffs = [];

        // 1. Площадь
        const nspdArea = parseFloat(nspdProps.area || nspdProps.params_area || nspdProps.specified_area || 0);
        const localArea = parseFloat(localProps.area || 0);
        if (Math.abs(nspdArea - localArea) > 0.01) {
            newDiffs.push({
                label: 'Площадь',
                local: localArea + ' м²',
                nspd: nspdArea + ' м²',
                field: 'area',
                newValue: nspdArea
            });
        }

        // 2. Тип
        const nspdType = nspdProps.category_id === '36368' ? 'plot' : 'building';
        if (nspdType !== localProps.type) {
            newDiffs.push({
                label: 'Тип объекта',
                local: localProps.type === 'plot' ? 'Участок' : 'Здание',
                nspd: nspdType === 'plot' ? 'Участок' : 'Здание',
                field: 'type',
                newValue: nspdType
            });
        }

        // 3. Адрес
        const nspdAddress = nspdProps.address || '';
        const localAddress = localProps.address || '';
        if (nspdAddress && nspdAddress !== localAddress) {
            newDiffs.push({
                label: 'Адрес',
                local: localAddress || '(пусто)',
                nspd: nspdAddress,
                field: 'address',
                newValue: nspdAddress
            });
        }

        // 4. Геометрия
        if (nspdObj.geometry && feature.geometry) {
            const nspdGeomStr = JSON.stringify(nspdObj.geometry.coordinates);
            const localGeomStr = JSON.stringify(feature.geometry.coordinates);
            
            if (nspdGeomStr !== localGeomStr) {
                newDiffs.push({
                    label: 'Геометрия',
                    local: 'Существующая',
                    nspd: 'Новая (изменена)',
                    field: 'geometry',
                    newValue: nspdObj.geometry
                });
            }
        }

        setDiffs(newDiffs);
    };

    const handleApply = async () => {
        if (!nspdData) return;
        
        setLoading(true);
        try {
            // Подготавливаем обновленные свойства, сохраняя существующие локальные данные
            const updatedProperties = { 
                ...feature.properties,
                // Всегда обновляем технические поля из НСПД
                nspd_data: nspdData.properties
            };
            
            let updatedGeometry = feature.geometry;
            
            diffs.forEach(diff => {
                if (diff.field === 'geometry') {
                    updatedGeometry = diff.newValue;
                } else {
                    updatedProperties[diff.field] = diff.newValue;
                }
            });

            const updatedFeature = {
                ...feature,
                geometry: updatedGeometry,
                properties: updatedProperties
            };

            // Вызываем внешний обработчик обновления
            await onUpdate(updatedFeature);
            toast.success('Данные успешно обновлены из НСПД');
            onClose();
        } catch (err) {
            console.error(err);
            toast.error('Ошибка при обновлении объекта');
        } finally {
            setLoading(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <RotateIcon size={20} />
                        <span>Обновление данных из НСПД</span>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <XCircleIcon size={24} />
                    </button>
                </div>

                <div className={styles.body}>
                    <div className={styles.objectInfo}>
                        <strong>{feature.properties.cadastral_number}</strong>
                        <span>{feature.properties.name || 'Без имени'}</span>
                    </div>

                    {loading ? (
                        <div className={styles.statusBox}>
                            <RotateIcon size={32} className={styles.spin} />
                            <p>Запрашиваем актуальные данные...</p>
                        </div>
                    ) : error ? (
                        <div className={`${styles.statusBox} styles.error`}>
                            <AlertTriangleIcon size={32} />
                            <p>{error}</p>
                        </div>
                    ) : diffs.length > 0 ? (
                        <div className={styles.diffSection}>
                            <p className={styles.diffHint}>Найдены расхождения в данных:</p>
                            <table className={styles.diffTable}>
                                <thead>
                                    <tr>
                                        <th>Свойство</th>
                                        <th>Текущее</th>
                                        <th>НСПД (новое)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {diffs.map((diff, idx) => (
                                        <tr key={idx}>
                                            <td className={styles.fieldLabel}>{diff.label}</td>
                                            <td className={styles.localVal}>{diff.local}</td>
                                            <td className={styles.nspdVal}>{diff.nspd}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className={styles.statusBox}>
                            <CheckCircleIcon size={32} color="#10b981" />
                            <p>Данные объекта полностью соответствуют записям в НСПД.</p>
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    <button className={styles.cancelBtn} onClick={onClose}>Отмена</button>
                    {diffs.length > 0 && (
                        <button className={styles.applyBtn} onClick={handleApply} disabled={loading}>
                            {loading ? 'Обновление...' : 'Применить изменения'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NSPDUpdateModal;
